import { getPlanDisplayName } from './planUtils';
import { isLiveSubscription } from './subscriptionState';

// ── What we tell someone after they cancel or reactivate ────────────────────
//
// Both messages used to be one flat sentence that left the important questions
// unanswered:
//
//   cancel      "Subscription will be cancelled at the end of your billing
//                period (Sep 19, 2026)"
//   reactivate  "Starter Plan is active again. Billing continues as normal and
//                you were not charged today."
//
// Cancelling is the moment people are most anxious, and that sentence answered
// none of what they are anxious about: do I lose access now, what happens to
// the credits I have already paid for, and can I undo this? The reactivation
// one named a plan but not the date money next moves, and "billing continues as
// normal" is reassurance without information.
//
// Each message is now a short list where every line answers one question, in
// the order people ask them. They are built here, as pure functions over plain
// data, so the wording is testable and the two surfaces cannot drift.

/** A date someone can act on, or null when there is nothing definite to say. */
const dayLabel = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * The name the CUSTOMER knows a plan by.
 *
 * The API sends `subscription_plans.name`, which still holds the ORIGINAL names
 * — "Starter Plan" and "Pro Plan" — while every screen calls those plans
 * Optimize and Scale. Mapping here means these messages cannot be the one place
 * in the product that names a plan differently from the card just clicked.
 * (upgradeQuote.js does the same, for the same reason.)
 *
 * The trailing " Plan" is then dropped because the surrounding copy already says
 * the word: "the Optimize Plan plan" otherwise.
 *
 * Returns '' when unknown, so callers drop the detail rather than print a hole.
 */
const planLabel = (name) => {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return '';
  const display = getPlanDisplayName({ name: trimmed }) || trimmed;
  return String(display).replace(/\s+plan$/i, '');
};

/**
 * After a cancellation is scheduled.
 *
 * Three lines, in the order the questions arrive:
 *   1. do I lose access now?          — no, and here is the exact date
 *   2. what about credits I paid for? — they remain usable
 *   3. can I undo this?               — yes, until that same date
 *
 * The date appears twice on purpose. It is the answer to two different
 * questions, and making the reader carry it from the first line to the third is
 * how people end up believing the reactivation window is shorter than it is.
 */
export function cancellationConfirmedMessage({ planName, accessUntil } = {}) {
  const plan = planLabel(planName);
  const when = dayLabel(accessUntil);

  const stays = plan
    ? `Your ${plan} plan stays active until`
    : 'Your plan stays active until';

  // The FIRST date is emphasised: it is the one the reader is scanning for —
  // the day access actually ends. The second mention of it, in the undo line,
  // deliberately is not; bolding both would emphasise nothing and make the
  // sentence read as two separate deadlines rather than one date answering two
  // questions.
  const first = when
    ? [
      { text: `${stays} ` },
      { text: when, strong: true },
      { text: '. After that you will move to the Basic plan.' },
    ]
    : [{ text: `${stays} the end of your billing period. After that you will move to the Basic plan.` }];

  return [
    first,
    [{ text: 'Your remaining credits stay usable until they expire.' }],
    [{
      text: when
        ? `You can reactivate any time before ${when} and nothing changes.`
        : 'You can reactivate any time before then and nothing changes.',
    }],
  ];
}

/**
 * After a cancellation is undone.
 *
 * "No charge today" leads because it is the first thing people check when a
 * subscription action completes, and the next payment date follows it so the
 * reassurance is anchored to a fact rather than left as a promise.
 *
 * The credit line is omitted entirely when the balance is unknown — inventing
 * "Your 0 credits were preserved" would be alarming and, on a reactivation,
 * almost certainly false.
 */
export function reactivationConfirmedMessage({ planName, nextBillingDate, credits } = {}) {
  const plan = planLabel(planName);
  const when = dayLabel(nextBillingDate);

  const back = plan
    ? `You're back on the ${plan} plan.`
    : "You're back on your plan.";

  // "No charge today" is bold for the same reason it is on the downgrade
  // dialogs: undoing a cancellation is a billing action, and the first thing
  // anyone checks is whether it cost them anything.
  const paragraphs = [[
    { text: `${back} ` },
    { text: 'No charge today', strong: true },
    {
      text: when
        ? ` — your next payment is due on ${when}.`
        : ' — billing continues on your usual date.',
    },
  ]];

  if (typeof credits === 'number' && Number.isFinite(credits) && credits > 0) {
    paragraphs.push([{ text: `Your ${credits.toLocaleString()} credits were preserved.` }]);
  }

  return paragraphs;
}

/**
 * The downgrade dialog, BEFORE the user commits.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 * The generic plan-change body, which rendered five fragments about a
 * transaction where no money moves:
 *
 *   Total due today
 *   $0.00
 *   No charge today.
 *   Then $9.50 on September 19, 2026.
 *   This change takes effect on September 19, 2026. Until then nothing about
 *   your plan changes.
 *   Charged to VISA •••• 4242.
 *   Your monthly allowance becomes 220 credits on September 19, 2026. The
 *   credits you have now are not affected before then.
 *
 * A "$0.00 total" and a card on file are the vocabulary of a purchase; this is
 * a scheduled change. Three paragraphs, one question each: when does it happen,
 * what does it cost me now, and what changes when it lands.
 *
 * ── ONE DATE ────────────────────────────────────────────────────────────────
 * `effectiveDate` is the renewal date — when the new plan starts AND when the
 * next payment is taken. Every date here is that one date, so the dialog cannot
 * contradict itself. (The post-commit confirmation additionally names the day
 * before, as the last day on the old plan; that distinction is worth making
 * once the change is real, not while the user is still deciding.)
 *
 * Returns paragraphs of SEGMENTS — `[[{ text, strong }, …], …]` — rendered by
 * RichMessage. A joined string could not do either thing this dialog needs: a
 * blank line inside a <Typography> collapses, and "There will be no charge
 * today" has to carry weight, because on a screen about changing plans the
 * first thing anyone looks for is whether they are about to be billed.
 */
export function downgradeQuoteMessage({
  fromPlan, toPlan, effectiveDate, currentCredits, nextAmountCents, newAllotment, currency = 'usd',
} = {}) {
  const from = planLabel(fromPlan);
  const to = planLabel(toPlan);
  const when = dayLabel(effectiveDate);
  const n = (v) => Number(v).toLocaleString();

  const money = (cents) => {
    if (typeof cents !== 'number' || !Number.isFinite(cents)) return null;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: String(currency || 'usd').toUpperCase(),
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const paragraphs = [];

  // 1. What is being scheduled, and that nothing changes yet.
  const move = from && to
    ? `Your downgrade from the ${from} to the ${to} plan`
    : 'Your downgrade';
  const keeps = from ? `You keep the ${from} plan until then.` : 'You keep your current plan until then.';
  paragraphs.push([{
    text: when
      ? `${move} will be scheduled for ${when}. ${keeps}`
      : `${move} will be scheduled for the end of your billing period. ${keeps}`,
  }]);

  // 2. Cost today, and the credits already paid for. "There will be no charge
  //    today" is bold because it is the question the reader actually opened
  //    this dialog with. The credit clause is dropped rather than zeroed when
  //    the balance could not be read — telling someone their "current 0
  //    credits" are safe is worse than saying nothing.
  const hasCredits = typeof currentCredits === 'number'
    && Number.isFinite(currentCredits) && currentCredits > 0;
  paragraphs.push([
    { text: 'There will be no charge today', strong: true },
    {
      text: hasCredits
        ? `, and your current ${n(currentCredits)} credits will remain unaffected.`
        : ', and your current credits will remain unaffected.',
    },
  ]);

  // 3. What actually changes on the day. Both halves are conditional because a
  //    price with no allowance (or the reverse) is still a useful sentence.
  const price = money(nextAmountCents);
  const hasAllotment = typeof newAllotment === 'number'
    && Number.isFinite(newAllotment) && newAllotment > 0;
  const onThatDay = when ? `On ${when} your` : 'Your';

  if (price && hasAllotment) {
    paragraphs.push([{
      text: `${onThatDay} price will become ${price} per month and you will start receiving `
        + `${n(newAllotment)} credits at every renewal.`,
    }]);
  } else if (price) {
    paragraphs.push([{ text: `${onThatDay} price will become ${price} per month.` }]);
  } else if (hasAllotment) {
    paragraphs.push([{
      text: `${onThatDay} plan will include ${n(newAllotment)} credits at every renewal.`,
    }]);
  }

  return paragraphs;
}

/**
 * The pay-as-you-go prompt under the plan tiers on Manage Plan.
 *
 * ── WHY IT IS THERE ─────────────────────────────────────────────────────────
 * That screen offered three subscriptions and nothing else, while credit packs
 * were reachable only from the Add Credits button on the balance card — the
 * screen the user has just navigated AWAY from to get here. Two opposite
 * audiences hit the same dead end:
 *
 *   free trial / Basic plan  wants credits WITHOUT a subscription
 *   paid plan               has run out mid-month and needs topping up, not a
 *                           bigger tier
 *
 * So the question differs but the destination does not — both open the same
 * package picker the Credits page uses.
 *
 * ── WHY isLiveSubscription DECIDES ──────────────────────────────────────────
 * The free tier holds no subscription row by design, so it returns false for
 * exactly the trial/Free audience. It also fails SAFE: a paying customer whose
 * status we do not recognise keeps the paid wording rather than being asked
 * whether they would prefer to pay as you go.
 *
 * Returns { question, action } rather than one string so the caller can render
 * the question as text and the action as the clickable part.
 */
export function creditPacksPrompt(subscription) {
  return {
    question: isLiveSubscription(subscription)
      ? 'Need more credits this month?'
      : 'Prefer to pay as you go?',
    action: 'Buy credit packs',
  };
}

/**
 * The credit-pack dialog's subtitle, and its confirm button.
 *
 * Both audiences buy the same packs, but for opposite reasons, and the line
 * under the title is where that difference belongs:
 *
 *   free / trial  is being told this is a real alternative to subscribing
 *   paid          is being told the top-up lands NOW, not at the next renewal
 *
 * The button differs because the two are paying differently: a subscriber is
 * charged on the card already on file and never leaves the dialog, while a
 * free-plan account goes to Stripe Checkout — see /buy-credits.
 *
 * `amountLabel` is the formatted total for the selected pack; the button names
 * it so the customer sees the figure on the control that commits them to it.
 * Omitted when nothing is selected yet.
 *
 * ── WHY THE PAID SUBTITLE NAMES THE PLAN BALANCE ────────────────────────────
 * "Credits will be added immediately" never said what they are added TO, which
 * is the one thing a customer about to spend money wants confirmed: that this
 * tops up what they already hold rather than replacing it.
 *
 * `planCredits` is the PLAN-granted balance, deliberately not the pooled total.
 * The sentence ends "and stay valid for 12 months" — true of the pack being
 * bought, and false of plan credits, which expire after two billing cycles (see
 * creditBreakdown.js BREAKDOWN_FOOTER). Naming the combined total here would
 * stretch that 12-month promise across credits it does not cover. It is also
 * the number the breakdown already labels "Plan credits", so the two agree.
 *
 * Dropped entirely when there is no usable figure — a balance that has not
 * loaded, or a plan balance of zero. A missing clause is invisible; "your
 * existing plan credit balance of 0 credits" is an own goal.
 */
export function creditPackDialog(subscription, amountLabel = null, { planCredits = null } = {}) {
  const paid = isLiveSubscription(subscription);

  const n = Number(planCredits);
  const known = Number.isFinite(n) && n > 0;
  const plural = n === 1 ? 'credit' : 'credits';
  const paidSubtitle = known
    ? `Credits will be added to your existing plan credit balance of ${n.toLocaleString()} ${plural} immediately and stay valid for 12 months.`
    : 'Credits will be added immediately and stay valid for 12 months.';

  return {
    title: 'Buy credit packs',
    // REQUESTED (Task 1): the free trial / Basic plan subtitle opens by saying
    // what kind of purchase this is, before what it does not need.
    // OLD: : 'No subscription needed. Credits stay valid for 12 months.',
    subtitle: paid
      ? paidSubtitle
      : 'This is a one-off purchase. No subscription needed. Credits stay valid for 12 months.',
    confirmLabel: paid
      ? (amountLabel ? `Confirm and pay ${amountLabel}` : 'Confirm and pay')
      : 'Continue to checkout',
    /** Subscribers pick a card here; free accounts pick one at Stripe. */
    showsPaymentMethod: paid,
    // REQUESTED (Task 1): a line under the packs, for the free tier only —
    // the honest trade-off beside a "no subscription needed" pitch. A
    // subscriber is already on the cheaper per-credit rate, so it would be
    // noise there. Null rather than absent so the dialog can test one key.
    footnote: paid ? null : 'Credit packs cost more per credit than a subscription.',
    // REQUESTED (Task 1): "Not now" on the free tier, which is being offered
    // an option rather than finishing a purchase it started. Subscribers keep
    // "Cancel".
    // OLD: the dismiss button was a literal 'Cancel' in CreditPackDialog.
    cancelLabel: paid ? 'Cancel' : 'Not now',
  };
}

/**
 * After a credit pack has been bought and paid for.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * A subscriber buying on a saved card never leaves the page, so there was no
 * moment that said the purchase worked: the dialog closed, the balance quietly
 * changed, and the customer was left to infer from a number that they had been
 * charged. The Checkout path had a confirmation all along, because the redirect
 * back gives it one for free — so the two ways of buying the same pack behaved
 * differently, and the newer one was the silent one. Both paths now render this
 * through the same SuccessDialog, so they cannot drift apart again.
 *
 * Up to three lines, in the order the questions arrive after paying:
 *   1. what did I just get, and what do I have now
 *   2. what did you take
 *   3. on a PAID plan: how long do these last, and when are they spent
 *
 * REQUESTED (again): on a paid plan, the third line reads "Credit pack credits
 * are valid for 12 months and are used after your plan credits." It was here
 * once ("Purchased credits are valid for …") and was dropped when this became
 * paragraphs, on the reasoning that the purchase modal states the 12 months
 * before payment. That misses half of it: a subscriber now holds two kinds of
 * credit, and which is spent first is worth saying the moment they have both.
 * Off a paid plan there are no plan credits for a pack to come after, so the
 * line is not shown there.
 *
 * Returned as PARAGRAPHS (see RichMessage) rather than a joined string: a
 * plain string rendered `white-space: pre-line` gets a line break between the
 * two lines but no real spacing, which read as cramped.
 *
 * `balanceAfter` comes from the SERVER, read back after the grant. Adding the
 * pack to the last balance the page happened to be holding would put a wrong
 * number in front of someone who has just paid; the clause is dropped entirely
 * when the server could not read it.
 */
export function creditPurchaseConfirmedMessage({
  credits, amountCents, balanceAfter, currency = 'usd',
  /**
   * Was the pack charged to a card already on file? False for a Stripe
   * Checkout purchase, where the card was typed in on Stripe's page — "your
   * saved card" is untrue there, and that is most free-tier purchases.
   */
  savedCard = true,
  /**
   * Is the buyer on a live PAID plan? Adds the validity line, which compares
   * the pack's credits with plan credits — see the note above.
   */
  onPaidPlan = false,
} = {}) {
  const n = (v) => Number(v).toLocaleString();

  const money = (cents) => {
    if (typeof cents !== 'number' || !Number.isFinite(cents)) return null;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: String(currency || 'usd').toUpperCase(),
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const added = Number(credits);
  const hasCredits = Number.isFinite(added) && added > 0;
  const hasBalance = typeof balanceAfter === 'number'
    && Number.isFinite(balanceAfter) && balanceAfter > 0;

  const paragraphs = [];

  if (hasCredits) {
    paragraphs.push([{
      text: hasBalance
        // REQUESTED (384): "xx credits added - your balance is now xxx."
        // OLD: `${n(added)} credits added — your new balance is now ${n(balanceAfter)} credits.`
        ? `${n(added)} credits added — your balance is now ${n(balanceAfter)} credits.`
        : `${n(added)} credits added to your account.`,
    }]);
  } else if (hasBalance) {
    paragraphs.push([{ text: `Your balance is now ${n(balanceAfter)} credits.` }]);
  }

  const charged = money(amountCents);
  // A zero is stated, not hidden: "we charged $0.00" would be alarming, but
  // silently omitting the amount on a purchase is worse.
  //
  // REQUESTED (384): the Stripe Checkout confirmation — the free trial / Basic
  // path — is the one line above and nothing else. Stripe's own page has just
  // shown the amount and emails the receipt. The saved-card path keeps the
  // charge line: nothing else on that path ever states what was taken.
  // OLD: if (charged) {
  //   paragraphs.push([{ text: `We charged ${charged} to your ${savedCard ? 'saved card' : 'card'}.` }]);
  // }
  if (charged && savedCard) {
    paragraphs.push([{ text: `We charged ${charged} to your saved card.` }]);
  }

  // Neither line above could be built — the server told us nothing usable.
  // An empty dialog body reads as broken; this reads as merely terse.
  if (paragraphs.length === 0) paragraphs.push([{ text: 'Your credit purchase was successful.' }]);

  // Last, and after the fallback: it is about the credits just described, so
  // it follows them rather than standing in for them.
  // OLD (8e79fa1, dropped in f64e322): 'Purchased credits are valid for 12
  // months and are used after your plan credits.'
  if (onPaidPlan) {
    paragraphs.push([{
      text: 'Credit pack credits are valid for 12 months and are used after your plan credits.',
    }]);
  }

  return paragraphs;
}

/**
 * After a subscription has been started through Stripe's hosted checkout.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * A trial or Basic account with no card on file subscribes on Stripe's page,
 * and the redirect back used to end in a green toast across the top of the
 * screen — "Payment successful! Your account has been updated." — gone in six
 * seconds. Every other plan change (an upgrade, a downgrade, a cancellation, a
 * reactivation) answers with the confirmation dialog, so the one that STARTS
 * the subscription was the only one that looked like a passing notification.
 * It now renders through the same SuccessDialog as the others.
 *
 * Three lines, in the order the questions arrive after paying:
 *   1. what am I on now                  — the plan, emphasised
 *   2. did I get what the plan includes  — the credits have landed
 *   3. when am I charged again           — the renewal date, emphasised
 *
 * ── WHAT IT DELIBERATELY DOES NOT SAY ───────────────────────────────────────
 * Every fact here is read back from the subscription row AFTER /verify-session
 * activated it; nothing is assumed from the card the customer clicked. Two
 * facts the other confirmations carry are absent for that reason:
 *   • the amount — /verify-session does not return what Stripe charged, and a
 *     price worked out here can disagree with the invoice (tax, the yearly
 *     price). The charge is on Recent Transactions, behind the dialog.
 *   • the credit figure — the grant follows the server's per-plan config, not
 *     a number on the subscription payload, so "N credits added" could be
 *     wrong. The balance card behind the dialog shows the real figure.
 * Nor does it promise a receipt: the checkout path queues no email of ours.
 *
 * A missing plan or date loses its clause rather than printing a hole.
 */
export function subscriptionStartedMessage({ planName, planSlug, nextBillingDate } = {}) {
  const plan = planLabel(planName) || (planSlug ? planLabel(planSlug) : '');
  const when = dayLabel(nextBillingDate);

  const paragraphs = [
    plan
      // The plan name is the emphasis: it is the single fact that changed.
      ? [{ text: "You're now on the " }, { text: `${plan} plan`, strong: true }, { text: '.' }]
      : [{ text: "You're now on your new plan." }],
    [{ text: 'Your plan credits have been added to your balance.' }],
  ];

  if (when) {
    paragraphs.push([
      { text: 'Your next payment is due on ' },
      { text: when, strong: true },
      { text: '.' },
    ]);
  }

  return paragraphs;
}

/**
 * Any other verified checkout — neither a credit pack nor a subscription.
 * Rare, but it gets the same dialog rather than the toast this replaced, and
 * keeps that toast's words.
 */
export function paymentConfirmedMessage() {
  return [[{ text: 'Your account has been updated.' }]];
}

/** Dialog titles, kept beside the bodies they head. */
export const CANCELLATION_CONFIRMED_TITLE = 'Cancellation confirmed';
export const REACTIVATION_CONFIRMED_TITLE = 'Subscription reactivated';
/**
 * Heads the post-commit downgrade message. Without it the dialog falls back to
 * the generic "Success", which is the wrong word for choosing a smaller plan.
 */
export const DOWNGRADE_SCHEDULED_TITLE = 'Downgrade scheduled';
/**
 * Heads the credit-purchase confirmation. Named for what happened rather than
 * the generic "Success", so the heading alone answers "did my payment work?".
 */
export const CREDIT_PURCHASE_CONFIRMED_TITLE = 'Credits added';
/**
 * Heads the subscription-started confirmation. The same words PlanSelector's
 * in-page first-subscription dialog uses, so starting a plan reads the same
 * whether the card was entered on our page or on Stripe's.
 */
export const SUBSCRIPTION_STARTED_TITLE = 'Subscription active';
/** Heads paymentConfirmedMessage — the words of the toast it replaces. */
export const PAYMENT_CONFIRMED_TITLE = 'Payment successful';

/**
 * sessionStorage key marking "a credit-pack Checkout redirect is in flight".
 *
 * ── WHY THIS EXISTS, NOT JUST THE `type=credits` URL PARAM ──────────────────
 * The buy-credits successUrl already carries `&type=credits` so Dashboard can
 * tell a credits purchase apart from a subscription checkout when Stripe sends
 * the browser back. But that URL is handed to a backend outside this repo,
 * which appends its own `session_id` before Stripe redirects to it — nothing
 * here can guarantee it preserves an unrelated query param it wasn't told to
 * keep. If it drops `type`, the redirect-back silently fell back to the old,
 * easy-to-miss toast — precisely the inconsistency this was meant to fix.
 *
 * sessionStorage is same-origin and untouched by that round trip: set right
 * before leaving for Stripe, read (and cleared) the moment we land back,
 * regardless of what the URL looks like by then. Kept alongside `type` as a
 * second, backend-independent signal rather than a replacement for it.
 */
export const PENDING_CREDIT_PURCHASE_KEY = 'improveseo:pendingCreditPurchase';

/**
 * Is this Stripe redirect-back a credit-pack purchase? Either signal is
 * enough — see PENDING_CREDIT_PURCHASE_KEY above for why there are two.
 *
 * Reads AND clears the sessionStorage flag: consumed the moment we land back
 * from ANY Stripe redirect, so a stale flag can't leak into a later,
 * unrelated one (e.g. a subscription checkout). sessionStorage access is
 * wrapped — some browser contexts (private mode, storage disabled) throw on
 * it — and a throw there falls back to the URL param alone rather than
 * crashing the redirect handler.
 *
 * `params` takes anything with `.get(name)` — a URLSearchParams in real use,
 * a plain object in tests.
 */
export function isCreditsPurchaseRedirect(params) {
  const type = params?.get?.('type');
  let pendingCreditPurchase = false;
  try {
    pendingCreditPurchase = sessionStorage.getItem(PENDING_CREDIT_PURCHASE_KEY) === '1';
    sessionStorage.removeItem(PENDING_CREDIT_PURCHASE_KEY);
  } catch { /* best-effort */ }
  return type === 'credits' || pendingCreditPurchase;
}

/**
 * What a free-plan account can do next.
 *
 * ── WHY IT IS HERE AND NOT WRITTEN TWICE ────────────────────────────────────
 * This sentence appears on two screens — the Active Plan card on Credits &
 * Subscriptions, and the plan block in Settings — and it has already been
 * through the failure this file exists to prevent. Both screens carried the
 * same line ("Your free trial has ended. Upgrade to restore full access — any
 * credits you purchased remain usable."), Settings was corrected first, and
 * for one release the product told the same customer two different things
 * about the same account. Written once, that cannot recur.
 *
 * ── WHY SEGMENTS AND NOT A STRING ───────────────────────────────────────────
 * Two fragments are links, and the two screens reach the same destinations by
 * different means: Settings is elsewhere in the app and navigates, while the
 * Credits card is already on the page the links point INTO — its pack picker
 * is a dialog it owns. So this returns the WORDS plus a named action, and each
 * screen decides how to travel. Neither can reword the sentence on its own.
 *
 * Actions are named rather than boolean so a third destination can be added
 * without every caller having to guess what a new flag means.
 */
export const FREE_PLAN_ACTION = {
  PLANS: 'plans',
  CREDIT_PACKS: 'credit_packs',
};

export function freePlanNextSteps() {
  return [
    { text: 'Get a paid subscription plan', action: FREE_PLAN_ACTION.PLANS },
    { text: ' or ' },
    { text: 'purchase a credit pack', action: FREE_PLAN_ACTION.CREDIT_PACKS },
    { text: ' to add credits.' },
  ];
}

/**
 * The two ways to get credits, explained at the top of Manage Plan.
 *
 * The screen opened straight onto three subscription cards and a text link to
 * credit packs, which answered "which plan?" for someone who had already
 * decided to subscribe and answered nothing for anyone still deciding whether
 * to. The card this feeds states the choice BEFORE the prices: a monthly
 * allowance, or one-off packs.
 *
 * ── WHY THE COPY IS HERE ────────────────────────────────────────────────────
 * Approved wording, reproduced exactly. It lives beside the rest of the
 * subscription copy for the reason this file exists: the sentence about credit
 * packs expiring "in 12 months" is the same fact the expiry banner and the pack
 * picker state, and three screens each holding their own version of a number
 * like that is how they come to disagree.
 *
 * ── WHY SEGMENTS ────────────────────────────────────────────────────────────
 * Same shape as freePlanNextSteps: one fragment inside the pay-as-you-go intro
 * is a link ("Basic plan"), and the caller decides how to travel — here, by
 * scrolling rather than navigating. Returning words plus a named target keeps
 * the sentence in one piece and out of the JSX.
 *
 * ── WHY EACH SIDE OWNS ITS HEADING ──────────────────────────────────────────
 * The heading sits INSIDE the left column rather than spanning the card. Both
 * columns are complete arguments, and one banner across the top makes the left
 * one look like the subject and the right one like a caveat on it — which is
 * the emphasis this card exists to avoid. Within a side, intro is prose,
 * points are the short declarative lines, and footnote is set apart because on
 * the pay-as-you-go side it is the COST, not another reassurance.
 */
/** Where a plan-options link sends the reader. Named, not boolean: a third
 *  destination should not require every caller to guess what a new flag means. */
export const PLAN_OPTION_TARGET = {
  SUBSCRIPTIONS: 'subscriptions',
  BASIC_PLAN: 'basic_plan',
};

/**
 * The header across the top of the plan-options card.
 *
 * REQUESTED as the card's own header. It sat inside the subscription column
 * for one revision, on the reasoning that a banner across the top makes the
 * left column the subject and the right one a caveat. The layout answers that
 * differently now: the header names the CARD — the two ways to buy — and each
 * column below it is headed by the question it answers, so neither is subject
 * to the other.
 */
export const PLAN_OPTIONS_HEADER = 'Your Plan Options - Add Credits & Unlock Features';

export function planOptions() {
  return [
    {
      key: 'subscription',
      eyebrow: 'SUBSCRIPTION',
      // REQUESTED: "Your Plan Options - Add Credits & Unlock Features" is the
      // CARD's header now (see PLAN_OPTIONS_HEADER), not this column's. This
      // side keeps the question it was already asking, which is what its own
      // heading should have been — it reads as the counterpart to the
      // pay-as-you-go column's question rather than a banner over both.
      heading: 'Are you serious about boosting your local SEO every month?',
      intro: [
        [{ text: 'Get a subscription with a monthly credit allowance.' }],
      ],
      points: [
        'There are three subscription plans: Grow, Optimize and Scale.',
        'The higher plans add bulk post generation and scheduled publishing.',
        'Monthly or yearly pricing, and you can cancel any time.',
      ],
      footnote: null,
      linkLabel: 'See subscription options',
      target: PLAN_OPTION_TARGET.SUBSCRIPTIONS,
    },
    {
      key: 'pay_as_you_go',
      eyebrow: 'PAY-AS-YOU-GO',
      heading: 'Only need fresh local SEO content every now and then?',
      intro: [
        [{ text: 'Choose our pay-as-you-go option.' }],
        [
          { text: 'Get the free ' },
          // Bracketed in the original ticket, where brackets have meant "link".
          { text: 'Basic plan', target: PLAN_OPTION_TARGET.BASIC_PLAN },
          { text: ' and buy credit packs as you need them.' },
        ],
      ],
      points: [
        'One-off purchases.',
        'Nothing renews.',
        'Nothing is charged again.',
        'Credits stay valid for 12 months.',
        'Any packs purchased during the trial carry over.',
      ],
      // Set apart from the points above it: the others are reassurances, and
      // this one is the cost of them. Burying it in the list would read as
      // another selling point.
      footnote: 'Credit packs cost more per credit than a subscription.',
      linkLabel: 'See pay-as-you-go option',
      target: PLAN_OPTION_TARGET.BASIC_PLAN,
    },
  ];
}

/**
 * The line under the plan name on the Active Plan card, for a PAID plan.
 *
 * It read "Monthly subscription · Full access" on every tier. "Full access" is
 * a claim about the top tier — the one with unlimited websites and priority
 * support — so saying it on Grow and Optimize told those customers they already
 * had everything, which is both untrue and the opposite of what the card should
 * do next to an upgrade button.
 *
 * ── WHY THE NAME AND NOT website_limit ──────────────────────────────────────
 * planFeatures.js identifies the top tier by `website_limit === null`, which is
 * the better test — it keys off capability rather than a label. That field is
 * not on the subscription payload (see the shape in SubscriptionSummary), so it
 * is not available here.
 *
 * getPlanDisplayName is the next best thing: it maps the database's own names
 * onto what the customer is shown, so "Pro" and "Enterprise" both resolve to
 * Scale and a rename in the database cannot silently drop the clause.
 *
 * It also fails in the SAFE direction. An unrecognised plan resolves to
 * something that is not Scale, so it loses "Full access" rather than gaining
 * it — a missing boast, not a false promise.
 */
export function activePlanSubtitle({ planName, planSlug } = {}) {
  const base = 'Monthly subscription';
  const display = getPlanDisplayName({ name: planName, slug: planSlug });
  return display === TOP_TIER_DISPLAY_NAME ? `${base} · Full access` : base;
}

/** The tier "Full access" belongs to, as the customer knows it. */
export const TOP_TIER_DISPLAY_NAME = 'Scale';

/**
 * What someone is about to buy, stated before they leave for Stripe.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * A first subscription with no saved card goes straight to Stripe's hosted
 * checkout — no confirmation in between. That page renders the Stripe PRODUCT,
 * and every plan shares one, so it reads:
 *
 *     Subscribe to ImproveSEO Subscription
 *     $9.50 per month
 *     Subsciption For improve seo
 *
 * The typo is theirs, live. The real problem is that it names no plan: someone
 * who clicked Grow is asked to pay for something they have never heard of, and
 * the credits they are buying are not mentioned at all.
 *
 * Fixing that page means giving each plan its own Stripe Product — Stripe will
 * not let a Checkout session override a recurring Price's product name. That is
 * admin-server and Stripe-account work, and until it happens this is what the
 * customer has: the same facts, in our words, at the moment of the decision
 * rather than after it.
 *
 * Every figure is read, never typed. `credits` is the plan's own grant and
 * `pieces` comes from GET /credits/estimate, so a pricing change moves this line
 * instead of stranding it — the failure that left "10 free credits of each
 * type" on the trial copy through two changes to the credit model.
 */
export function checkoutPreamble({
  planName, planSlug, priceCents, credits, pieces: estimate = null, currency = 'usd',
} = {}) {
  const plan = planLabel(planName) || (planSlug ? planLabel(planSlug) : '');

  let price = null;
  if (typeof priceCents === 'number' && Number.isFinite(priceCents)) {
    try {
      price = new Intl.NumberFormat('en-US', {
        style: 'currency', currency: String(currency || 'usd').toUpperCase(),
      }).format(priceCents / 100);
    } catch {
      price = `$${(priceCents / 100).toFixed(2)}`;
    }
  }

  const n = Number(credits);
  const hasCredits = Number.isFinite(n) && n > 0;
  // Dropped until the server has answered, rather than printing "about NaN pieces".
  const pieces = hasCredits && Number(estimate) > 0 ? Number(estimate) : null;

  let detail = null;
  if (hasCredits && pieces) {
    detail = `You will receive ${n.toLocaleString()} credits per month, which equals about `
      + `${pieces.toLocaleString()} piece${pieces === 1 ? '' : 's'} of local SEO content with AI image.`;
  } else if (hasCredits) {
    detail = `You will receive ${n.toLocaleString()} credits per month.`;
  }

  return {
    title: plan ? `Subscribe to the ${plan} plan` : 'Confirm your subscription',
    price: price ? `${price} per month` : null,
    detail,
  };
}

/**
 * What happens after the trial, and the two ways forward.
 *
 * REQUESTED behind "Choose a Plan" on the Active Plan card WHILE A TRIAL IS
 * RUNNING. That button used to navigate straight to the plan grid, which
 * answers "which subscription?" for someone who has not yet been told they do
 * not need one — the trial ends on the free Basic plan whatever they do, and
 * nothing is lost when it does. Saying so first is the point of the dialog;
 * the two buttons under it are the options, not the answer.
 *
 * ── WHY THIS IS NOT planOptions() ───────────────────────────────────────────
 * The options card on Manage Plan makes the same choice, and this is
 * deliberately not that copy reused. That card is read by someone who has
 * already arrived to buy; this is read by someone whose trial is ending, so it
 * opens with what happens if they do nothing, and it carries one line the
 * other must not — the value comparison between the two kinds of credit.
 * Sharing the strings would mean every future edit to either had to be
 * correct for both audiences.
 *
 * @param trialEndsOn  the formatted end date, or null when it is unknown
 */
export function trialOptionsDialog({ trialEndsOn = null } = {}) {
  return {
    title: 'Your options after the trial',
    // The date is dropped rather than printed as "N/A" or "Invalid Date" when
    // the server has not given us one: "will end" is still true and still
    // useful, and a broken date beside a promise about money is worse than no
    // date at all.
    intro: trialEndsOn
      ? `Your free trial will end on ${trialEndsOn} and you will automatically move to `
        + 'the free Basic plan. Nothing will be charged and nothing will be removed — you '
        + "keep every feature and everything you've published."
      : 'Your free trial will end and you will automatically move to the free Basic plan. '
        + 'Nothing will be charged and nothing will be removed — you keep every feature '
        + "and everything you've published.",
    lead: 'From there you have two options:',
    options: [
      {
        key: 'subscription',
        // The lead-in is bold in the dialog, so it is its own field: the rest
        // is one paragraph and must not carry the weight.
        label: 'A subscription',
        text: '— a monthly credit allowance. There are three plans: Grow, Optimize and '
          + 'Scale. The higher plans add bulk post generation and scheduled publishing. '
          + 'Monthly or yearly pricing, and you can cancel any time.',
        buttonLabel: 'Get subscription',
      },
      {
        key: 'credit_packs',
        label: 'Credit packs',
        text: '— one-off purchases. Nothing renews, nothing is charged again, and the '
          + 'credits stay for 12 months. Any packs you buy during the trial carry over.',
        // Kept out of the paragraph above: it is the cost of the option rather
        // than another of its reassurances, the same separation the options
        // card draws with its footnote.
        note: 'Note: subscription credits give you more value than credit pack credits.',
        buttonLabel: 'Buy credit pack',
      },
    ],
  };
}
